import { clsx } from 'clsx'
import { Button } from 'component/Button'
import { CircleButton } from 'component/Button/CircleButton'
import { Img } from 'component/Image'
import { Gap, Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useEffectOnce } from 'hook/useEffectOnce'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { ChangeEvent, useRef } from 'react'
import { createPortal } from 'react-dom'
import { opt } from 'store/opt'
import { store } from 'store/store'
import css from './FullCameraPane.module.scss'

type Back = () => void
type Done = () => Promise<unknown>

type Props = {
  onBack: Back
  onDone: Done
}

function turn() {
  switch (opt.turn) {
    case 0:
      return css.turn0
    case 90:
      return css.turn90
    case 180:
      return css.turn180
    case 270:
      return css.turn270
    default:
      return css.turn0
  }
}

export const FullCameraPane = observer(({ onBack, onDone }: Props) => {
  const { mobile } = store
  const { camera, userPhoto } = store.profile.common
  const { starting, processing, seconds, canTake } = camera
  const busy = starting || processing
  const file = useRef<HTMLInputElement>(null)
  const src = userPhoto.src
  const done = !!userPhoto.url
  const retake = !!src || camera.error
  const take_disabled = retake ? false : busy || !canTake
  const mirror = opt.mirror && camera.isFrontCamera

  const camera_class = clsx(css.video, turn(), mirror && css.mirror, camera.stream && css.stream)
  const scan_class = clsx(css.scan, userPhoto.uploading ? css.busy : css.none)

  useEffectOnce(() => {
    return () => camera.stop()
  })

  const onVideo = useLatestCallback((element: HTMLVideoElement | null) => {
    if (!element) return
    camera.element = element
    void camera.start()
  })

  const onFile = useLatestCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    userPhoto.setFile(file)
  })

  const onUpload = useLatestCallback(() => {
    file.current?.click()
  })

  const onTake = useLatestCallback(async () => {
    if (src) {
      userPhoto.clear()
      camera.start()
    } else {
      await camera.countdown(5)
      camera.take(userPhoto)
    }
  })

  const onSwitch = useLatestCallback(async () => {
    await camera.switchCamera()
  })

  const onRepeat = useLatestCallback(() => {
    userPhoto.setUrl(undefined)
  })

  const onContinue = useLatestCallback(async () => {
    await onDone()
  })

  return <Vertical className={css.pane}>
    <Space className={css.back}>
      <Space size={56} center onClick={onBack}>
        <Icon size={20} name="chevron_left" />
      </Space>
    </Space>

    {!camera.error && <video key={camera.key} ref={onVideo} className={camera_class} />}

    {src && <Space className={css.image}>
      <Img src={src} />
    </Space>}

    <Space className={css.center} center>
      {!src && camera.error && <Vertical center>
        <Icon size={24} name="videocam_off" />
        <Gap height={24} />
        <Typo size={14} semi center>
          {i18n(mobile ? 'CannotAccessCamera' : 'CameraUnavailable')}
        </Typo>
        <Gap height={4} />
        <Typo size={12} center>
          {i18n(mobile ? 'PleaseCheckYourPermissions' : 'PleaseCheckYourHardwareSettings')}
        </Typo>
      </Vertical>}

      <span className={clsx(css.corner, css.tl)} />
      <span className={clsx(css.corner, css.tr)} />
      <span className={clsx(css.corner, css.bl)} />
      <span className={clsx(css.corner, css.br)} />

      {!!seconds && (
        <Space className={css.countdown} center>
          <Typo size={96} bold one>
            {seconds.toFixed()}
          </Typo>
        </Space>
      )}

      {true && <Space className={scan_class} />}
    </Space>

    {mobile && !done && <Space className={css.circles} gap={16} pad={16}>
      <CircleButton className={css.circle} size={48} action={onUpload}>
        <Icon size={16} name="upload_2" fill />
      </CircleButton>
      <CircleButton className={retake ? css.circle2 : css.circle3} size={64}
        disabled={take_disabled} action={onTake}>
        <Icon size={24} name="photo_camera" fill />
      </CircleButton>
      <CircleButton className={css.circle} size={48} action={onSwitch}>
        <Icon size={16} name="autorenew" fill />
      </CircleButton>
    </Space>}

    {!mobile && !done && <Space gap={16} pad={16} spread>
      <Button className={css.gray} gap={8} compact large upper action={onUpload}>
        <Icon size={16} name="upload_2" fill />
        <Typo line={16}>
          {i18n('UploadFile')}
        </Typo>
      </Button>
      <Button className={retake ? css.gray : css.white} gap={8} compact large upper
        disabled={take_disabled} action={onTake}>
        <Icon size={16} name="photo_camera" fill />
        <Typo line={16}>
          {i18n('TakePhoto')}
        </Typo>
      </Button>
    </Space>}

    {done && <Space gap={16} pad={16} minHeight={mobile ? 96 : 72} spread>
      <Button className={css.gray} gap={8} compact large upper action={onRepeat}>
        <Icon size={16} name="autorenew" />
        <Typo line={16}>
          {i18n('Repeat')}
        </Typo>
      </Button>
      <Button className={css.white} gap={8} compact large upper action={onContinue}>
        <Typo line={16}>
          {i18n('Continue')}
        </Typo>
        <Icon size={16} name="chevron_right" />
      </Button>
    </Space>}

    <input ref={file} className={css.file} type="file" accept="image/*" onChange={onFile} />
  </Vertical>
})

export function renderFullCameraPane(back: Back, done: Done) {
  return createPortal(<FullCameraPane onBack={back} onDone={done} />, document.body)
}
